import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import FileField from "../../forms/fields/FileField";
import OptionField from "../../forms/fields/OptionField";
import FormField from "../../forms/fields/FormField";
import SaporeForm from "../../forms/SaporeForm";
import ArticlesFormSet from "../../forms/fields/ArticlesFormSet";
import ArticleForm from "../../forms/fields/ArticleForm";
import TextField from "../../forms/fields/TextField";
import {default as handleErrorToast} from "../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../components/toast/SuccessToast";
import {formatDate} from "../../utils/DateHelper";
import {ORDERS_PATH} from "../../routes";

export default class OrderFeature {
    constructor(application, navigator, id) {
        this._application = application;
        this._navigator = navigator;
        this._loaded = false;
        this._articlesAreUpToDate = false;
        this._activeDialog = false
        this._form = null;
        this._articlesForm = null;
        this._customerOptions = [];
        this._deliverySiteOptions = [];
        this._customerArticlePriceList = null;
        this._customerDebtAlert = null;
        this._creationDate = '25/02/2024';
        this._lastEditionDate = '25/02/2024';
        this._order = null;
        this._order_id = id;
        this._enabled_dates = []
        this._isFreeOrder = false;
        this._authorization = null;
        this._advancePaymentProof = null;
        this._advancePaymentRequired = false;
        this._advancePaymentProofForm = null;
        this._isDeletingOrder = false;
        this._observations = "";
        this._orderPrices = null;

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            form: computed,
            _form: observable,
            _advancePaymentProofForm: observable,
            _activeDialog: observable,
            activeDialog: computed,
            articlesForm: computed,
            _articlesForm: observable,
            _updateArticlesForm: action,
            _order: observable,
            _customerDebtAlert: observable,
            customerDebtAlert: computed,
            _authorization: observable,
            _customerOptions: observable,
            customerOptions: computed,
            _deliverySiteOptions: observable,
            deliverySiteOptions: computed,
            _updateOptions: action,
            _enabled_dates: observable,
            order: computed,
            authorization: computed,
            isFormValid: computed,
            _finishLoading: action,
            _isFreeOrder: observable,
            isFreeOrder: computed,
            _articlesAreUpToDate: observable,
            articlesAreUpToDate: computed,
            _isDeletingOrder: observable,
            isDeletingOrder: computed,
            _observations: observable,
            observations: computed,
            setIsDeletingOrder: action,
            setObservations: action,
            _orderPrices: observable,
            orderPrices: computed,
            _advancePaymentProof: observable,
            _advancePaymentRequired: observable,
        })
    }

    async load() {
        await this.getOrder()
        await this.getOptions()
        this.setInitializeFreeOrderFromInitialOrder()
        this._createForm()
        this._finishLoading()
    }

    async aValueChanged() {
        const canSaveOrder = this.canEditOrder() && this.orderForm() && this.orderForm().isValid;
        if (canSaveOrder) {
            if (this.canSaveDeliverySite()) {
                await this.editOrder()
            }
            if (this.canSavePurchaseOrdersNumber()) {
                await this.editOrdersPurchaseOrderNumber()
            }
        }
    }

    async getOrder() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._order = response.order()
                this._order_id = this._order.number
                await this._getCustomerArticlePriceList()
                this._getCustomerDebtAlert()
                this._createArticlesForm()
                const order = this.order;
                if(!this.orderIsInvoiced() && this.orderIsOnInitialState()) {
                    this._getEnabledDatesFor(order.deliverySite.deliverySiteCode, order.customer.businessName, order.purchaseOrderNumber, false)
                }
                this._authorization = response.authorization()
                this._advancePaymentProof = response.advancePaymentProof()
                this._advancePaymentRequired = response.advancePaymentRequired()
                this._createAdvancePaymentProofForm()
                await this.getOrderPrices()
            },
            handlesError: (response) => {
                response.errors().map(error => {
                    this.showAlert(error);
                })
            }
        });
        return this._application.apiClient().getOrder(this._order_id, responseHandler);
    }

    async updateOrderArticles() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._order = response.order()
                this._order_id = this._order.number
                this._createArticlesForm()
                await this.getOrderPrices()
            },
            handlesError: (response) => {
                response.errors().map(error => {
                    this.showAlert(error);
                })
            }
        });
        return this._application.apiClient().getOrder(this._order_id, responseHandler);
    }

    deliverySiteFieldsAreReady() {
        return this.hasDeliverySiteNameValue() &&
            this.hasDeliverySiteAddressValue() &&
            this.hasDeliverySiteCityValue() &&
            this.hasCustomerBusinessNameValue()
    }

    showAlert(error) {
        const errorCode = error.code
        const data = error.data

        if (errorCode === 'duplicated_editable_order') {
            handleErrorToast(`Ya existe un Pedido en estado ${this.stateTextFrom(data.state)} para el Punto de Entrega ${data.delivery_site_name}` +
                ` con fecha de entrega ${data.delivery_date}. El mismo fue creado el día ${data.creation_date} por el usuario` +
                ` ${data.creator_name}. Podes editarlo desde el listado de pedidos.`)

        } else if (errorCode === 'duplicated_non_editable_order') {
            handleErrorToast(`Ya existe una Nota de Pedido en estado ${this.stateTextFrom(data.state)} para el Punto de Entrega ${data.delivery_site_name}` +
                ` con fecha de entrega ${data.delivery_date}. El mismo fue creado el día ${data.creation_date} por el usuario` +
                ` ${data.creator_name}. El mismo ya no puede modificarse.`)

        } else if (errorCode === 'incorrect_delivery_date_for_delivery_site') {
            handleErrorToast("No se puede entregar este pedido para esa fecha.")

        } else if (errorCode === 'order_was_deleted') {
            handleErrorToast("Este pedido no existe o fue eliminado.")

        } else {
            handleErrorToast("No se pudo crear el pedido.")
        }
    }

    stateTextFrom(state) {
        return state === 'INITIAL' ? "Abierto" :
            state === 'PENDING' ? "Cerrado" :
                state === 'CONFIRMED' ? "Proceso de Facturación" :
                    state === 'INVOICED' ? "Facturada" :
                        state === 'REVISION' ? 'En Revisión' :
                            state === 'REJECTED' ? 'Rechazado' :
                                "Desconocido"

    }

    async addArticleToOrder(article) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                await this.updateOrderArticles()
                this._articlesAreUpToDate = true
                handleSuccessToast("Los artículos fueron guardados correctamente.")
            },
            handlesError: (response) => {
                this._articlesAreUpToDate = true
                handleErrorToast("No se pudieron guardar los artículos.")
            }
        });
        this._articlesAreUpToDate = false
        const orderNumber = this._order.number;
        article = this.addDiscountToArticle(article)
        this._application.apiClient().addArticlesToOrder(orderNumber, [article], responseHandler);
    }

    async deleteOrdersArticle(article) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                await this.getOrder()
                handleSuccessToast("El artículo fue eliminado correctamente.")
            },
            handlesError: (response) => {
                handleErrorToast("No se eliminar el artículo.")
            }
        });
        const orderNumber = this._order.number;
        this._application.apiClient().deleteOrdersArticle(orderNumber, article.code, responseHandler);
    }

    async markOrderAsPending() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                handleSuccessToast(this.toastMessageWhenMarkingOrderAsPending(response))
                this._navigator(ORDERS_PATH)
            },
            handlesError: (response) => {
                if (response.isAdvancePaymentRequiredError()) {
                    handleErrorToast("Debe cargar un comprobante de pago antes de pasar el pedido a cerrado.");
                } else {
                    handleErrorToast("No se pudo pasar a cerrada.");
                }
            }
        });
        const orderNumber = this._order.number;
        this._application.apiClient().markOrderAsPending(orderNumber, responseHandler);
    }

    toastMessageWhenMarkingOrderAsPending(response) {
        return response.state() === "PENDING" ? "La orden fue pasada a cerrada." :
            response.state() === "REVISION" ? "Está orden requiere autorización y se encuentra en revisión" :
                "Operación realizada éxitosamente"
    }

    async editOrder() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._order = response.order()
                await this._getCustomerDebtAlert()
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron guardar los cambios.")
            }
        });
        const ordersNumber = this._order.number;
        const order = this.orderForm().values;
        this._application.apiClient().editOrder(ordersNumber, order, responseHandler);
    }

    async editOrdersPurchaseOrderNumber() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._order = response.order()
            },
            handlesError: (response) => {
                handleErrorToast("No fue posible guardar el número de la orden de compra.")
            }
        });
        const ordersNumber = this._order.number;
        const purchaseOrdersNumber = this.purchaseOrdersNumberField().value
        this._application.apiClient().editOrdersPurchaseOrderNumber(ordersNumber, purchaseOrdersNumber, responseHandler);
    }

    freeOrderCommentField() {
        return this.getFieldByName("freeOrderComment");
    }

    canEditFreeOrderComment() {
        return this.isFreeOrder && (this.orderIsOnInitialState() || this.orderIsInRevisionState())
    }

    orderIsInRevisionState() {
        return this._order.state === 'REVISION'
    }

    async editOrdersFreeOrderComment() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._order = response.order()
                handleSuccessToast("El comentario fue guardado correctamente.")
            },
            handlesError: (response) => {
                handleErrorToast("No fue posible guardar el comentario.")
            }
        });
        const orderNumber = this._order.number;
        const observations = this.freeOrderCommentField().value;
        this._application.apiClient().editOrdersObservations(orderNumber, observations, responseHandler);
    }

    async returnOrderToInitial() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                handleSuccessToast("El pedido fue pasado a abierto.")
                this.getOrder()
            },
            handlesError: (response) => {
                handleErrorToast("No se pudo pasar el pedido a abierto.")
            }
        });
        const orderNumber = this._order.number;
        this._application.apiClient().returnOrderToInitial(orderNumber, responseHandler);
    }

    isDateValidForDelivery(aDateTime) {
        return this._enabled_dates.some(date => {
            const enabledDate = formatDate(date)
            const calendarDate = formatDate(new Date(aDateTime))
            return enabledDate === calendarDate
        });
    }

    async getOptions() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._updateOptions(response.customerOptions(), response.deliverySiteOptions());
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener los clientes")
            }
        });
        // TODO: reify partialOrder when order edition has to be implemented. Now the order form has all the order data,
        // we have to separate the customer and the delivery site in two forms
        let partialOrder;
        if (this.orderForm()) {
            partialOrder = this.orderForm().values;
        } else {
            const order = this.order
            const customer = order.customer
            const deliverySite = order.deliverySite
            partialOrder = {
                "businessName": customer.businessName,
                "fantasyName": customer.fantasyName,
                "customerCode": customer.code,
                "deliverySiteName": deliverySite.deliverySiteName,
                "deliverySiteAddress": deliverySite.deliverySiteAddress,
                "deliverySiteCity": deliverySite.deliverySiteCity,
            }
        }
        return this._application.apiClient().getOrderOptions(partialOrder, responseHandler)
    }

    _updateOptions(customerOptions, deliverySiteOptions) {
        this._customerOptions = customerOptions;
        this._deliverySiteOptions = deliverySiteOptions;
    }

    _getCustomerArticlePriceList() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._customerArticlePriceList = response.articlePriceList();
            },
            handlesError: (response) => {
                const customerHasNoArticlePriceList = response.errors().some((error) => error.code === 'no_article_price_list_available_for_customer');
                if (customerHasNoArticlePriceList) {
                    handleErrorToast("No se pudieron obtener los precios de los artículos");
                }
            }
        });
        const customerBusinessName = this._order.customer.businessName;
        return this._application.apiClient().getCustomerArticlePriceList(customerBusinessName, responseHandler);
    }


    _getCustomerDebtAlert() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._customerDebtAlert = response.debtAlert();
            },
            handlesError: (response) => {
                handleErrorToast("No se pudo obtener el estado de deuda del cliente.");
            }
        });
        const customerBusinessName = this._order.customer.businessName;
        return this._application.apiClient().customerDebtAlert(customerBusinessName, responseHandler);
    }

    get customerDebtAlert() {
        return this._customerDebtAlert;
    }

    _getEnabledDatesFor(deliverySiteCode, customerBusinessName, purchaseOrderNumber, showSuccessToast = true) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._enabled_dates = response.responseEnabledDates();
                if (showSuccessToast) {
                    handleSuccessToast("Se obtuvieron las fechas habilitadas para el punto de entrega.")
                }
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener las fechas habilitadas para el punto de entrega.")
            }
        });
        return this._application.apiClient().getEnabledDatesForDeliverySite(deliverySiteCode, customerBusinessName, purchaseOrderNumber, this._order.number, responseHandler)
    }

    _getEnabledDatesForDeliverySite() {
        const deliverySiteCode = this.getFieldByName("deliverySiteCode").value
        const customerBusinessName = this.getFieldByName("businessName").value
        const purchaseOrderNumber = this.getFieldByName("purchaseOrderNumber").value
        return this._getEnabledDatesFor(deliverySiteCode, customerBusinessName, purchaseOrderNumber)
    }

    _createForm() {
        const businessNames = this._businessNames()
        const customerCodes = this._customerCodes()
        const fantasyNames = this._fantasyNames()
        const deliverySiteNames = this._deliverySiteNames()
        const deliverySiteAddresses = this._deliverySiteAddresses()
        const deliverySiteCities = this._deliverySiteCities()
        const deliverySiteCodes = this._deliverySiteCodes()
        const order = this.order
        const customer = order.customer
        const deliverySite = order.deliverySite
        // TODO: The form has to receive the order and consume the fields values from it
        const fields = {
            "businessName": new OptionField(businessNames, true, this, customer.businessName),
            "purchaseOrderNumber": new TextField(2, 20, true, order.purchaseOrderNumber, this),
            "customerCode": new OptionField(customerCodes, true, this, customer.code),
            "fantasyName": new OptionField(fantasyNames, true, this, customer.fantasyName),
            "deliverySiteName": new OptionField(deliverySiteNames, true, this, deliverySite.deliverySiteName),
            "deliverySiteAddress": new OptionField(deliverySiteAddresses, true, this, deliverySite.deliverySiteAddress),
            "deliverySiteCity": new OptionField(deliverySiteCities, true, this, deliverySite.deliverySiteCity),
            "deliverySiteCode": new OptionField(deliverySiteCodes, true, this, deliverySite.code),
            "deliveryDate": new FormField(true, order.deliveryDate, this),
            "freeOrderComment": new TextField(0, 1000, true, order.observations, this),
        }
        this._form = new SaporeForm(fields);
    }

    _createArticlesForm() {
        const articlesFormSet = new ArticlesFormSet([], this);
        this._updateArticlesForm(articlesFormSet)
        const articles = this.order.articles;
        for (const article of articles) {
            this.addExistentArticle(article)
        }
    }

    addExistentArticle(article) {
        this.articlesForm.addForm(new ArticleForm(
            {
                codeValues: this.articleCodesValues(),
                nameValues: this.articleNamesValues()
            },
            true,
            article))
    }

    updateOptionFields() {
        const fields = this.orderForm().fields()
        fields["businessName"].updateOptions(this._businessNames())
        fields["customerCode"].updateOptions(this._customerCodes())
        fields["fantasyName"].updateOptions(this._fantasyNames())
        fields["deliverySiteName"].updateOptions(this._deliverySiteNames())
        fields["deliverySiteAddress"].updateOptions(this._deliverySiteAddresses())
        fields["deliverySiteCity"].updateOptions(this._deliverySiteCities())
        fields["deliverySiteCode"].updateOptions(this._deliverySiteCodes())
    }

    async clearDeliverySiteDateAndPurchaseOrderNumber() {
        this.deliverySiteNameField().clear()
        this.getFieldByName("deliverySiteAddress").clear()
        this.getFieldByName("deliverySiteCity").clear()
        this.getFieldByName("deliverySiteCode").clear()
        this.deliveryDateField().clear()
        this.getFieldByName("purchaseOrderNumber").clear()
        await this.updateFormOptions()
    }

    orderForm() {
        return this.form;
    }

    _getCustomerFieldValues(customerField) {
        return this._customerOptions[customerField]
    }

    _getDeliverySiteFieldValues(deliverySiteField) {
        return this._deliverySiteOptions[deliverySiteField]
    }

    _deliverySiteCities() {
        return this._getDeliverySiteFieldValues("deliverySiteCity");
    }

    _deliverySiteCodes() {
        return this._getDeliverySiteFieldValues("deliverySiteCode")
    }

    deliverySiteCityOptions() {
        return this.optionsFor(this._deliverySiteCities());
    }

    _deliverySiteAddresses() {
        return this._getDeliverySiteFieldValues("deliverySiteAddress");
    }

    deliverySiteAddressOptions() {
        return this.optionsFor(this._deliverySiteAddresses());
    }

    _deliverySiteNames() {
        return this._getDeliverySiteFieldValues("deliverySiteName");
    }

    deliverySiteNameOptions() {
        return this.optionsFor(this._deliverySiteNames());
    }

    _fantasyNames() {
        return this._getCustomerFieldValues("fantasyName");
    }

    fantasyNameOptions() {
        return this.optionsFor(this._fantasyNames());
    }

    _customerCodes() {
        return this._getCustomerFieldValues("code");
    }

    customerCodeOptions() {
        return this.optionsFor(this._customerCodes());
    }

    _businessNames() {
        return this._getCustomerFieldValues("businessName");
    }

    businessNameOptions() {
        return this.optionsFor(this._businessNames());
    }

    optionsFor(values) {
        return values.map(value => {
            return {label: value, value: value}
        });
    }

    get isFormValid() {
        return this.orderForm().isValid && this.articlesForm.isValid;
    }

    get form() {
        return this._form;
    }

    set form(newForm) {
        this._form = newForm;
    }

    get activeDialog() {
        return this._activeDialog
    }

    set activeDialog(newDialog) {
        this._activeDialog = newDialog
    }

    get isDeletingOrder() {
        return this._isDeletingOrder
    }

    set isDeletingOrder(isDeletingOrder) {
        this._isDeletingOrder = isDeletingOrder
    }

    setIsDeletingOrder(isDeletingOrder) {
        this._isDeletingOrder = isDeletingOrder
    }

    get observations() {
        return this._observations
    }

    set observations(observations) {
        this._observations = observations
    }

    setObservations(observations) {
        this._observations = observations
    }

    hasObservations() {
        return this.observations.trim()
    }

    setActiveDialog(dialog) {
        this.activeDialog = dialog;
    }

    closeDialog() {
        this.activeDialog = null;
    }

    creationDate() {
        return this._creationDate
    }

    lastEditionDate() {
        return this._lastEditionDate
    }

    get loaded() {
        return this._loaded;
    }

    get articlesAreUpToDate() {
        return this._articlesAreUpToDate;
    }

    _finishLoading() {
        this._loaded = true;
        this._articlesAreUpToDate = true;
    }

    get articlesForm() {
        return this._articlesForm;
    }

    set articlesForm(form) {
        this._articlesForm = form;
    }

    addArticle() {
        this.articlesForm.addForm(new ArticleForm(
            {
                codeValues: this.articleCodesValues(),
                nameValues: this.articleNamesValues()
            },
            false))
    }

    _getArticleFieldValues(articleField) {
        return this._customerArticlePriceList.map(pricedArticle => {
            return pricedArticle.article[articleField]
        })
    }

    hasArticlePriceList() {
        return this._customerArticlePriceList !== null
    }

    articleCodesValues() {
        return this._getArticleFieldValues('code')
    }

    articleCodesOptions() {
        return this.optionsFor(this._getArticleFieldValues('code'))
    }

    articleNamesValues() {
        return this._getArticleFieldValues('name')
    }

    articleNamesOptions() {
        return this.optionsFor(this._getArticleFieldValues('name'))
    }

    get order() {
        return this._order
    }

    get authorization() {
        return this._authorization
    }

    authorizationRequiresChanges() {
        return this.hasAuthorization() && this._authorization.state === 'Changes Required';
    }

    authorizationObservations() {
        if (!this.hasAuthorization()) {
            return '';
        }
        return this._authorization.observations || '';
    }

    shouldShowAuthorizationObservations() {
        return this.authorizationRequiresChanges() && this.authorizationObservations().trim().length > 0;
    }

    advancePaymentRequired() {
        return this._advancePaymentRequired;
    }

    advancePaymentProofUrl() {
        return this._advancePaymentProof && this._advancePaymentProof.file && this._advancePaymentProof.file.url;
    }

    advancePaymentProofForm() {
        return this._advancePaymentProofForm;
    }

    _createAdvancePaymentProofForm() {
        if (!this._advancePaymentRequired) {
            this._advancePaymentProofForm = null;
            return;
        }
        const initialValue = this._advancePaymentProofInitialValue();
        this._advancePaymentProofForm = new SaporeForm({
            advancePaymentProof: new FileField(0, 1, false, initialValue, null),
        }, null, null);
    }

    _advancePaymentProofInitialValue() {
        return this._advancePaymentProof && this._advancePaymentProof.file && this._advancePaymentProof.file.url ? this._advancePaymentProof.file : null;
    }

    _advancePaymentProofFormValueHasFile(value) {
        return value instanceof File || (value && value.url);
    }

    async uploadAdvancePaymentProofFromForm() {
        if (!this._advancePaymentProofForm) return;
        const value = this._advancePaymentProofForm.getFieldByName('advancePaymentProof').value;
        if (value instanceof File) {
            await this.uploadAdvancePaymentProof(value);
        } else if (this._advancePaymentProof && !this._advancePaymentProofFormValueHasFile(value)) {
            await this.deleteAdvancePaymentProof();
        }
    }

    async updateFormOptions() {
        await this.getOptions()
        this.updateOptionFields()
        await this.getEnabledDatesForDeliverySiteIfDeliverySiteWasCompleted()
    }

    async getEnabledDatesForDeliverySiteIfDeliverySiteWasCompleted() {
        if (this.deliverySiteFieldsAreReady()) {
            await this._getEnabledDatesForDeliverySite()
        }
    }

    get customerOptions() {
        return this._customerOptions
    }

    get deliverySiteOptions() {
        return this._deliverySiteOptions
    }

    canEditCustomer() {
        return false && this.canEditOrder()
    }

    _updateArticlesForm(articlesForm) {
        this._articlesForm = articlesForm
    }

    canEditArticles() {
        return this.orderIsOnInitialState();
    }

    canMarkOrderAsPending() {
        const baseConditions = this._form.isValid && this.order.articles.length > 0 && this.orderIsOnInitialState() && this.hasArticlePriceList();
        if (this.advancePaymentRequired()) {
            return baseConditions && !!this.advancePaymentProofUrl();
        }
        return baseConditions;
    }

    async uploadAdvancePaymentProof(file) {
        const orderNumber = this._order.number;
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async () => {
                handleSuccessToast("Comprobante de pago cargado exitosamente.");
                await this.getOrder();
            },
            handlesError: () => {
                handleErrorToast("No se pudo procesar el comprobante de pago. Intente nuevamente.");
            }
        });
        return this._application.apiClient().registerAdvancePaymentProof(orderNumber, file, responseHandler);
    }

    async deleteAdvancePaymentProof() {
        const orderNumber = this._order.number;
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async () => {
                handleSuccessToast("Comprobante de pago eliminado.");
                await this.getOrder();
            },
            handlesError: () => {
                handleErrorToast("No se pudo eliminar el comprobante de pago. Intente nuevamente.");
            }
        });
        return this._application.apiClient().registerAdvancePaymentProof(orderNumber, null, responseHandler);
    }

    canSaveDeliverySite() {
        return this.deliverySiteFieldsAreReady() && this.hasDeliveryDateValue() &&
            (this.deliverySiteNameField().value !== this.order.deliverySite.deliverySiteName ||
                !this.deliveryDateField().value.isSame(this.order.deliveryDate));
    }

    canSavePurchaseOrdersNumber() {
        return this.purchaseOrdersNumberField().value !== this.order.purchaseOrderNumber
    }

    purchaseOrdersNumberField() {
        return this.getFieldByName("purchaseOrderNumber");
    }

    canEditDeliverySite() {
        return this.canEditOrder()
    }

    canEditDeliveryDate() {
        return this.canEditOrder() && this.deliverySiteFieldsAreReady()
    }

    canEditOrder() {
        return this.orderIsOnInitialState() && this.hasArticlePriceList()
    }

    orderIsOnInitialState() {
        return this._order.state === 'INITIAL'
    }

    orderIsBeingTransmitted() {
        return this._order.state === 'TRANSMITTING'
    }

    orderHasBeenCreated() {
        return this.orderNumber() !== '-'
    }

    pricedArticleWithCode(articleCode) {
        if (this.orderIsInvoiced()) {
            for (let invoiceItem of this._order.articles) {
                if (invoiceItem.code === articleCode) {
                    return this._presentedInvoiceItem(invoiceItem);
                }
            }
        } else {
            for (let priced_article of this._customerArticlePriceList) {
                if (priced_article.article.code === articleCode) {
                    return priced_article
                }
            }
        }
    }

    pricedArticleWithName(articleName) {
        if (this.orderIsInvoiced()) {
            for (let invoiceItem of this._order.articles) {
                if (invoiceItem.name === articleName) {
                    return this._presentedInvoiceItem(invoiceItem);
                }
            }
        } else {
            for (let priced_article of this._customerArticlePriceList) {
                if (priced_article.article.name === articleName) {
                    return priced_article
                }
            }
        }
    }

    _presentedInvoiceItem(invoiceItem) {
        return {
            article: {
                category: invoiceItem.family,
                code: invoiceItem.code,
                fantasyName: invoiceItem.fantasyName,
                itemsAmount: invoiceItem.amount,
                name: invoiceItem.name
            },
            price: invoiceItem.unitPrice
        };
    }

    articleWithName(articleName) {
        return this.pricedArticleWithName(articleName).article
    }

    articleWithCode(articleCode) {
        return this.pricedArticleWithCode(articleCode).article
    }

    hasDeliverySiteNameValue() {
        return this.orderForm() && this.deliverySiteNameField().hasValue
    }

    deliverySiteNameField() {
        return this.getFieldByName("deliverySiteName");
    }

    deliverySiteCodeField() {
        return this.getFieldByName("deliverySiteCode");
    }

    hasDeliverySiteAddressValue() {
        return this.orderForm() && this.getFieldByName("deliverySiteAddress").hasValue
    }

    hasDeliverySiteCityValue() {
        return this.orderForm() && this.getFieldByName("deliverySiteCity").hasValue
    }

    hasCustomerBusinessNameValue() {
        return this.orderForm() && this.getFieldByName("businessName").hasValue
    }

    hasDeliveryDateValue() {
        return this.orderForm() && this.deliveryDateField().hasValue
    }

    deliveryDateField() {
        return this.getFieldByName("deliveryDate");
    }

    getFieldByName(fieldName) {
        return this.orderForm().getFieldByName(fieldName);
    }

    orderNumber() {
        return this.order.number;
    }

    canReturnToInitialState() {
        return (this._order.state === 'PENDING' || this._order.state === 'FAILED_TO_CONFIRMED') && this.hasNotFinishedAuthorizationProcess()
    }

    hasAuthorization() {
        return this._authorization && Object.keys(this._authorization).length > 0;
    }

    hasNotFinishedAuthorizationProcess() {
        const authorization = this._authorization;

        const doesNotHaveAuthorization = !authorization || Object.keys(authorization).length === 0;
        const isPending = authorization.state === 'Pending';
        const requiresChanges = authorization.state === 'Changes Required';

        return doesNotHaveAuthorization || isPending || requiresChanges;
    }

    canDeleteOrder() {
        return this._order.state !== 'CONFIRMED' && !this.orderIsBeingTransmitted() && !this.orderIsInvoiced() && this._order.state !== 'REJECTED'
    }

    orderIsInvoiced() {
        return this._order.state === 'INVOICED'
    }

    deleteOrder() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._navigator(ORDERS_PATH)
                handleSuccessToast("El pedido fue eliminado correctamente.")
            },
            handlesError: (response) => {
                handleErrorToast("No se pudo eliminar el pedido.")
            }
        });
        this._application.apiClient().deleteOrder(this._order.number, this.observations, responseHandler);
    }

    getEnabledDatesForDeliverySiteWithPurchaseOrderNumber() {
        if (this.deliverySiteFieldsAreReady()){
            this.deliveryDateField().clear()
            this._getEnabledDatesForDeliverySite()
        }
    }

    get isFreeOrder() {
        return this._isFreeOrder
    }

    setInitializeFreeOrderFromInitialOrder(){
        this._isFreeOrder = !!this.order.isFree
    }

    addDiscountToArticle(article) {
        return {
            ...article,
            discount: this._isFreeOrder ? 100 : 0
        }
    }

    async getOrderPrices() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._orderPrices = response.orderPrices();
            },
        });
        if (this._order && this._order.number) {
            return this._application.apiClient().orderPrices(this._order.number, responseHandler)
        }
    }

    get orderPrices() {
        return this._orderPrices;
    }

    subtotal() {
        return this._orderPrices ? this._orderPrices.articlesTotalPrice : 0;
    }

    totalTaxes() {
        return this._orderPrices ? this._orderPrices.totalVAT : 0;
    }

    totalGrossIncomeTaxes() {
        return this._orderPrices ? this._orderPrices.totalGrossIncomeTaxes : 0;
    }

    total() {
        return this._orderPrices ? this._orderPrices.total : 0;
    }

}
